import styles from "./Modals.module.scss";
import React, { useEffect, useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { getUrlOrigin } from "@whatslly/commons/urlUtils";

const BASE_DATA: Destination = {
  type: "crowdstrike" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: { apiKey: "", apiUrl: "" },
};
type FormDataType = FormDestinationDataType<"crowdstrike">;

export const CrowdstrikeModalForm: React.FC<DestinationFormProps<"crowdstrike">> = ({ onSubmitForm, settings, destinationType }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<Omit<FormDataType, "namespaces"> & { namespaces?: string[] }>>(null);
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);

  const validate = async (values: Omit<FormDataType, "namespaces"> & { namespaces?: string[] }) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.apiUrl) {
      errors.apiUrl = t("field_is_required");
    }
    if (!values.apiKey) {
      errors.apiKey = t("field_is_required");
    }
    if (!errors.apiKey && !getUrlOrigin(values.apiKey)) {
      errors.apiKey = t("invalid_url");
    }
    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    const apiUrl = getUrlOrigin(data.apiUrl);
    return {
      integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
      type: destinationType,
      settings: { ...data, apiUrl },
    };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  return (
    <>
      <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.api_key")} required />
          <FormPasswordTextInput name={"apiKey"} paddingInline="none" />
        </div>
        <div className={styles.field}>
          <Label className={styles.filedLabel} text={t("destinations_page.api_url")} required />
          <FormTextInput name={"apiUrl"} paddingInline="none" />
        </div>
      </FormElement>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </>
  );
};
